/**
 * Minimal NIfTI-1 reader/writer. Ported from openads-web/lib/wasm/nifti.ts and
 * extended with gzip transparently via the browser's native
 * DecompressionStream — every file this app touches (.nii.gz) needs it, so it
 * is not optional here the way it was in the source project.
 */

import { axisPermutation, orientationFromAffine, permuteAxes, reorient, type Orientation } from "./volume";

const HEADER_SIZE = 348;
const VOX_OFFSET = 352;
const DT_UINT8 = 2;
const DT_INT16 = 4;
const DT_INT32 = 8;
const DT_FLOAT32 = 16;
const DT_FLOAT64 = 64;
const DT_UINT16 = 512;

export interface NiftiVolume {
  dims: [number, number, number];
  pixdim: [number, number, number];
  /** Voxels as float32 in F-order: idx = x + y*nx + z*nx*ny. scl_slope/inter applied. */
  voxels: Float32Array;
  /** 4x4 voxel->world (RAS) affine, row-major, from sform if set else qform-diagonal. */
  affine: Float32Array;
  datatype: number;
}

export async function gunzipIfNeeded(bytes: Uint8Array): Promise<Uint8Array> {
  if (bytes[0] === 0x1f && bytes[1] === 0x8b) {
    const ds = new DecompressionStream("gzip");
    const stream = new Blob([bytes as BlobPart]).stream().pipeThrough(ds);
    const buf = await new Response(stream).arrayBuffer();
    return new Uint8Array(buf);
  }
  return bytes;
}

export async function loadNiftiFile(file: File | Blob): Promise<NiftiVolume> {
  const raw = new Uint8Array(await file.arrayBuffer());
  const bytes = await gunzipIfNeeded(raw);
  return reorientVolumeToRAS(readNifti(bytes));
}

const RAS: Orientation = ["R", "A", "S"];

/**
 * Reorients a volume's voxel data (and affine) to RAS+, matching
 * `nib.as_closest_canonical()` — which every ads_edge/OpenADS reference
 * script calls immediately after `nib.load()`, before any array-index-based
 * processing.
 *
 * ROOT CAUSE this fixes: the bundled ads_edge MNI demo's ground-truth mask
 * (`Stroke_space-MNI152_aff.nii.gz`) is stored with an LPI affine (confirmed
 * via `nib.aff2axcodes` -> ('L','P','I'), diagonal (-1,-1,-1)), while the
 * DWI/ADC/TTP/symclassic files in the same folder are already RAS. Skipping
 * this step meant the ground-truth array (loaded in its raw on-disk LPI
 * voxel order) and the predicted mask (computed in the DWI file's own RAS
 * voxel order) were being compared index-for-index across two DIFFERENT
 * orientations — a full 3-axis inversion. Predicted lesion volume matched
 * the Python reference almost exactly (both pipelines run the identical
 * ONNX model on identical, correctly-oriented model input), but Dice against
 * ground truth came back 0.0000 in the browser vs 0.884 in Python, because
 * Python's `load_ras()` canonicalizes every file (DWI, ADC, GT alike) before
 * comparing, and this app didn't. Loading every volume through this function
 * makes every array RAS-consistent the same way, independent of what
 * orientation any individual input file happens to be stored in.
 */
export function reorientVolumeToRAS(vol: NiftiVolume): NiftiVolume {
  const orientation = orientationFromAffine(vol.affine);
  if (orientation[0] === RAS[0] && orientation[1] === RAS[1] && orientation[2] === RAS[2]) return vol;

  const { srcAxis, flip } = axisPermutation(orientation, RAS);
  const { data, dims } = reorient(vol.voxels, vol.dims, orientation, RAS);
  const pixdim = permuteAxes(vol.pixdim, orientation, RAS);

  // New affine column d = old affine column srcAxis[d], sign-flipped if flip[d]
  // (moving +1 along new axis d is moving -1 along the old axis when flipped).
  const oldA = vol.affine;
  const newA = new Float32Array(16);
  for (let d = 0; d < 3; d++) {
    const s = srcAxis[d];
    const sign = flip[d] ? -1 : 1;
    for (let r = 0; r < 3; r++) newA[r * 4 + d] = oldA[r * 4 + s] * sign;
  }
  // Translation: world position of new voxel (0,0,0), i.e. the old affine
  // applied to whichever old voxel coordinate maps to the new origin.
  const originVoxel = [0, 0, 0];
  for (let d = 0; d < 3; d++) {
    const s = srcAxis[d];
    originVoxel[s] = flip[d] ? vol.dims[s] - 1 : 0;
  }
  for (let r = 0; r < 3; r++) {
    newA[r * 4 + 3] =
      oldA[r * 4 + 0] * originVoxel[0] + oldA[r * 4 + 1] * originVoxel[1] + oldA[r * 4 + 2] * originVoxel[2] + oldA[r * 4 + 3];
  }
  newA[15] = 1;

  return { dims, pixdim, voxels: data, affine: newA, datatype: vol.datatype };
}

export function readNifti(bytes: Uint8Array): NiftiVolume {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const LE = view.getInt32(0, true) === HEADER_SIZE;
  if (!LE && view.getInt32(0, false) !== HEADER_SIZE) {
    throw new Error("not a NIfTI-1 volume (bad sizeof_hdr)");
  }

  const dims: [number, number, number] = [
    view.getInt16(42, LE),
    view.getInt16(44, LE),
    view.getInt16(46, LE),
  ];
  const pixdim: [number, number, number] = [
    view.getFloat32(80, LE),
    view.getFloat32(84, LE),
    view.getFloat32(88, LE),
  ];
  const datatype = view.getInt16(70, LE);
  const voxOffset = view.getFloat32(108, LE) || VOX_OFFSET;
  let slope = view.getFloat32(112, LE);
  const inter = view.getFloat32(116, LE);
  if (slope === 0 || !isFinite(slope)) slope = 1;

  const n = dims[0] * dims[1] * dims[2];
  const voxels = new Float32Array(n);
  let o = voxOffset;
  const read = (): number => {
    switch (datatype) {
      case DT_UINT8: { const v = view.getUint8(o); o += 1; return v; }
      case DT_INT16: { const v = view.getInt16(o, LE); o += 2; return v; }
      case DT_INT32: { const v = view.getInt32(o, LE); o += 4; return v; }
      case DT_FLOAT32: { const v = view.getFloat32(o, LE); o += 4; return v; }
      case DT_FLOAT64: { const v = view.getFloat64(o, LE); o += 8; return v; }
      case DT_UINT16: { const v = view.getUint16(o, LE); o += 2; return v; }
      default:
        throw new Error(`unsupported NIfTI datatype ${datatype}`);
    }
  };
  for (let i = 0; i < n; i++) voxels[i] = read() * slope + inter;

  const affine = new Float32Array(16);
  affine[15] = 1;
  if (view.getInt16(254, LE) > 0) {
    for (let r = 0; r < 3; r++) {
      for (let c = 0; c < 4; c++) affine[r * 4 + c] = view.getFloat32(280 + r * 16 + c * 4, LE);
    }
  } else {
    affine[0] = pixdim[0];
    affine[5] = pixdim[1];
    affine[10] = pixdim[2];
  }

  return { dims, pixdim, voxels, affine, datatype };
}

// ---------------------------------------------------------------------------
// 4D (x,y,z,t) support — added for the PWI/TTP stage. `readNifti`/
// `loadNiftiFile` above only ever read dims[0..2] (hardcoded 3D), which is
// correct for every other stage in this app (all single-volume) but can't
// represent a raw 4D PWI time series. This is purely additive: nothing above
// this line changes shape or behaviour.
// ---------------------------------------------------------------------------

export interface NiftiVolume4D {
  dims: [number, number, number, number]; // x,y,z,t
  /** [dx,dy,dz] in mm, TR in seconds (NIfTI pixdim[4], byte offset 92). */
  pixdim: [number, number, number, number];
  /** F-order: idx = x + y*nx + z*nx*ny + t*nx*ny*nz. scl_slope/inter applied. */
  voxels: Float32Array;
  affine: Float32Array;
  datatype: number;
}

export function readNifti4D(bytes: Uint8Array): NiftiVolume4D {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const LE = view.getInt32(0, true) === HEADER_SIZE;
  if (!LE && view.getInt32(0, false) !== HEADER_SIZE) {
    throw new Error("not a NIfTI-1 volume (bad sizeof_hdr)");
  }

  const nx = view.getInt16(42, LE);
  const ny = view.getInt16(44, LE);
  const nz = view.getInt16(46, LE);
  const nt = Math.max(1, view.getInt16(48, LE));
  const dims: [number, number, number, number] = [nx, ny, nz, nt];
  const pixdim: [number, number, number, number] = [
    view.getFloat32(80, LE),
    view.getFloat32(84, LE),
    view.getFloat32(88, LE),
    view.getFloat32(92, LE), // TR, seconds (pixdim[4])
  ];
  const datatype = view.getInt16(70, LE);
  const voxOffset = view.getFloat32(108, LE) || VOX_OFFSET;
  let slope = view.getFloat32(112, LE);
  const inter = view.getFloat32(116, LE);
  if (slope === 0 || !isFinite(slope)) slope = 1;

  const n = nx * ny * nz * nt;
  const voxels = new Float32Array(n);
  let o = voxOffset;
  const read = (): number => {
    switch (datatype) {
      case DT_UINT8: { const v = view.getUint8(o); o += 1; return v; }
      case DT_INT16: { const v = view.getInt16(o, LE); o += 2; return v; }
      case DT_INT32: { const v = view.getInt32(o, LE); o += 4; return v; }
      case DT_FLOAT32: { const v = view.getFloat32(o, LE); o += 4; return v; }
      case DT_FLOAT64: { const v = view.getFloat64(o, LE); o += 8; return v; }
      case DT_UINT16: { const v = view.getUint16(o, LE); o += 2; return v; }
      default:
        throw new Error(`unsupported NIfTI datatype ${datatype}`);
    }
  };
  for (let i = 0; i < n; i++) voxels[i] = read() * slope + inter;

  const affine = new Float32Array(16);
  affine[15] = 1;
  if (view.getInt16(254, LE) > 0) {
    for (let r = 0; r < 3; r++) {
      for (let c = 0; c < 4; c++) affine[r * 4 + c] = view.getFloat32(280 + r * 16 + c * 4, LE);
    }
  } else {
    affine[0] = pixdim[0];
    affine[5] = pixdim[1];
    affine[10] = pixdim[2];
  }

  return { dims, pixdim, voxels, affine, datatype };
}

/** Same RAS canonicalization as `reorientVolumeToRAS`, applied per-timepoint (spatial reorientation only — t axis is untouched). See that function's header comment for why this matters. */
export function reorientVolume4DToRAS(vol: NiftiVolume4D): NiftiVolume4D {
  const orientation = orientationFromAffine(vol.affine);
  const [nx, ny, nz, nt] = vol.dims;
  if (orientation[0] === RAS[0] && orientation[1] === RAS[1] && orientation[2] === RAS[2]) return vol;

  const spatialN = nx * ny * nz;
  let outDims3: [number, number, number] = [nx, ny, nz];
  let out: Float32Array | null = null;
  for (let t = 0; t < nt; t++) {
    const frame = vol.voxels.subarray(t * spatialN, (t + 1) * spatialN) as Float32Array;
    const { data, dims } = reorient(frame, [nx, ny, nz], orientation, RAS);
    if (!out) {
      outDims3 = dims;
      out = new Float32Array(dims[0] * dims[1] * dims[2] * nt);
    }
    out.set(data, t * data.length);
  }
  const pixdim3 = permuteAxes([vol.pixdim[0], vol.pixdim[1], vol.pixdim[2]], orientation, RAS);

  const { srcAxis, flip } = axisPermutation(orientation, RAS);
  const oldA = vol.affine;
  const newA = new Float32Array(16);
  for (let d = 0; d < 3; d++) {
    const s = srcAxis[d];
    const sign = flip[d] ? -1 : 1;
    for (let r = 0; r < 3; r++) newA[r * 4 + d] = oldA[r * 4 + s] * sign;
  }
  const originVoxel = [0, 0, 0];
  for (let d = 0; d < 3; d++) {
    const s = srcAxis[d];
    originVoxel[s] = flip[d] ? vol.dims[s] - 1 : 0;
  }
  for (let r = 0; r < 3; r++) {
    newA[r * 4 + 3] =
      oldA[r * 4 + 0] * originVoxel[0] + oldA[r * 4 + 1] * originVoxel[1] + oldA[r * 4 + 2] * originVoxel[2] + oldA[r * 4 + 3];
  }
  newA[15] = 1;

  return {
    dims: [outDims3[0], outDims3[1], outDims3[2], nt],
    pixdim: [pixdim3[0], pixdim3[1], pixdim3[2], vol.pixdim[3]],
    voxels: out!,
    affine: newA,
    datatype: vol.datatype,
  };
}

export async function loadNiftiFile4D(file: File | Blob): Promise<NiftiVolume4D> {
  const raw = new Uint8Array(await file.arrayBuffer());
  const bytes = await gunzipIfNeeded(raw);
  return reorientVolume4DToRAS(readNifti4D(bytes));
}

/** Extracts timepoint `t` as an ordinary 3D NiftiVolume (e.g. to run SynthStrip on one frame of a 4D series). */
export function extractFrame3D(vol: NiftiVolume4D, t: number): NiftiVolume {
  const [nx, ny, nz] = vol.dims;
  const spatialN = nx * ny * nz;
  const voxels = vol.voxels.slice(t * spatialN, (t + 1) * spatialN);
  return { dims: [nx, ny, nz], pixdim: [vol.pixdim[0], vol.pixdim[1], vol.pixdim[2]], voxels, affine: vol.affine.slice(), datatype: vol.datatype };
}

/** Writes a float32 single-file NIfTI-1 (uncompressed) — used to hand results to NiiVue via a blob URL. */
export function writeNiftiFloat32(vol: { dims: [number, number, number]; pixdim: [number, number, number]; affine: Float32Array; voxels: Float32Array }): Uint8Array {
  const [nx, ny, nz] = vol.dims;
  const buf = new ArrayBuffer(VOX_OFFSET + vol.voxels.byteLength);
  const view = new DataView(buf);
  const LE = true;

  view.setInt32(0, HEADER_SIZE, LE);
  view.setInt16(40, 3, LE);
  view.setInt16(42, nx, LE);
  view.setInt16(44, ny, LE);
  view.setInt16(46, nz, LE);
  for (let i = 4; i <= 7; i++) view.setInt16(40 + i * 2, 1, LE);

  view.setInt16(70, DT_FLOAT32, LE);
  view.setInt16(72, 32, LE);

  view.setFloat32(76, 1, LE);
  view.setFloat32(80, vol.pixdim[0], LE);
  view.setFloat32(84, vol.pixdim[1], LE);
  view.setFloat32(88, vol.pixdim[2], LE);

  view.setFloat32(108, VOX_OFFSET, LE);
  view.setFloat32(112, 1, LE);
  view.setInt8(123, 2 | (8 << 3));

  view.setInt16(252, 1, LE);
  view.setInt16(254, 1, LE);
  for (let r = 0; r < 3; r++) {
    for (let c = 0; c < 4; c++) view.setFloat32(280 + r * 16 + c * 4, vol.affine[r * 4 + c], LE);
  }

  const magic = new Uint8Array(buf, 344, 4);
  magic.set([0x6e, 0x2b, 0x31, 0x00]);

  new Uint8Array(buf, VOX_OFFSET).set(new Uint8Array(vol.voxels.buffer, vol.voxels.byteOffset, vol.voxels.byteLength));
  return new Uint8Array(buf);
}
